import { useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { parseAsString, useQueryState } from 'nuqs'

import { orderService } from '@/services/order'
import { useAppSelector } from '@/store/hook'
import { selectDateFormat } from '@/store/selectors/configSelector'
import { useSessionId } from '@/hooks/useModifyPayloadIfHavingSessionBasedShoppingCart'
import { PAGE_SIZE, SORT_ORDERS } from '@/libs/constants'
import { dateUtils } from '@/libs/utils/dateUtils'
import { shouldRetryRequest } from '@/libs/utils/errorUtils'

const SORT_FIELD = {
  orderDate: 'orderDate',
}

const QUERY_CONFIG = {
  STALE_TIME: 5 * 60 * 1000, // 5 minutes
  GC_TIME: 10 * 60 * 1000, // 10 minutes
}

export function useOrdersQuery() {
  const [currentPage, setCurrentPage] = useQueryState(
    'page',
    parseAsString.withDefault('1'),
  )
  const [searchTerm, setSearchTerm] = useQueryState(
    'q',
    parseAsString.withDefault(''),
  )
  const [selectedStatus, setSelectedStatus] = useQueryState(
    'status',
    parseAsString.withDefault(''),
  )
  const [fromDate, setFromDate] = useQueryState(
    'from',
    parseAsString.withDefault(''),
  )
  const [toDate, setToDate] = useQueryState(
    'to',
    parseAsString.withDefault(''),
  )
  const [sortField, setSortField] = useQueryState(
    'sort',
    parseAsString.withDefault(`${SORT_FIELD.orderDate}.${SORT_ORDERS.descend}`),
  )

  const dateFormat = useAppSelector(selectDateFormat)
  const sessionId = useSessionId()

  const queryParams = useMemo(() => {
    const params = {
      pageNumber: currentPage,
      pageSize: PAGE_SIZE[10],
      orderBy: sortField.split('.')[1],
      sortBy: sortField.split('.')[0],
      searchTerm,
      status: selectedStatus,
      sessionId,
    }

    if (fromDate) {
      params.dateFrom = fromDate
    }
    if (toDate) {
      params.dateTo = toDate
    }

    return params
  }, [
    currentPage,
    sortField,
    searchTerm,
    selectedStatus,
    fromDate,
    toDate,
    sessionId,
  ])

  const queryKey = useMemo(
    () => [
      'orders',
      {
        page: currentPage,
        sort: sortField,
        search: searchTerm,
        status: selectedStatus,
        dateRange: { from: fromDate, to: toDate },
        sessionId,
      },
    ],
    [
      currentPage,
      sortField,
      searchTerm,
      selectedStatus,
      fromDate,
      toDate,
      sessionId,
    ],
  )

  const { data, isPending, isError } = useQuery({
    queryKey,
    queryFn: () => orderService.getOrders(queryParams),
    staleTime: QUERY_CONFIG.STALE_TIME,
    gcTime: QUERY_CONFIG.GC_TIME,
    refetchOnWindowFocus: false,
    enabled: !!dateFormat,
    retry: shouldRetryRequest,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30_000),
    placeholderData: (prev) => prev,
  })

  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page, { history: 'push' })
    },
    [setCurrentPage],
  )

  const handleSearch = useCallback(
    (value) => {
      setSearchTerm(value)
      setCurrentPage('1')
    },
    [setCurrentPage, setSearchTerm],
  )

  const handleFilterByStatus = useCallback(
    (value) => {
      setSelectedStatus(value)
      setCurrentPage('1')
    },
    [setCurrentPage, setSelectedStatus],
  )

  const handleDateChange = useCallback(
    (_, datesString) => {
      if (!datesString || (!datesString[0] && !datesString[1])) {
        setFromDate('')
        setToDate('')
        setCurrentPage('1', { history: 'push' })
        return
      }

      setFromDate(
        datesString[0]
          ? dateUtils.convertToISOString(datesString[0], dateFormat)
          : '',
      )
      setToDate(
        datesString[1]
          ? dateUtils.toInclusiveDate(datesString[1], dateFormat)
          : '',
      )
      setCurrentPage('1', { history: 'push' })
    },
    [dateFormat, setCurrentPage, setFromDate, setToDate],
  )

  const handleChangeSorter = useCallback(
    (_, __, sorter) => {
      const { field, order } = sorter

      setSortField(`${field}.${order}`)
      setCurrentPage('1')
    },
    [setCurrentPage, setSortField],
  )

  return {
    items: data?.items ?? [],
    totalCount: data?.totalCount ?? 0,
    isPending,
    isError,
    fromDate,
    toDate,
    searchTerm,
    selectedStatus,
    currentPage,
    handlePageChange,
    handleSearch,
    handleFilterByStatus,
    handleDateChange,
    handleChangeSorter,
  }
}
